import type { ParentChildQuizAttempt } from '@repo/types/parent';

import { useGetChildQuizAttempt } from '@repo/api/parent/useGetChildQuizAttempt';
import { format, parseISO } from 'date-fns';
import { TriangleAlertIcon } from 'lucide-react';

import { ParentGradeValue } from '@/components/features/parent/ParentGradeValue';
import { ParentRichText } from '@/components/features/parent/ParentRichText';
import { ParentStat } from '@/components/features/parent/ParentStat';
import QAttemptResultCard from '@/components/features/quizzes/QAttemptResultCard';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useParentDetailTarget } from '@/hooks/parent';

/** Same wording as the assignment dialog, so the two read as one family. */
const DATE_FORMAT = 'd MMM yyyy, H:mm';

/**
 * What the quiz row leaves out: the quiz's own terms — deadline, points on
 * offer, how many attempts were allowed — and its description.
 *
 * The attempt body is deliberately absent from the parent payload, so there are
 * no questions or answers to show here: a parent sees the outcome of the quiz,
 * not their child's working. The score dial is the same one the child sees, so
 * both read the result the same way.
 *
 * Which attempt it shows comes from the URL and is fetched by id, so a link opens
 * it whether or not the row it belongs to is on the page. One of these per page
 * is enough; the rows only write the link.
 */
export function ParentQuizAttemptDialog() {
  const target = useParentDetailTarget('quiz');

  const { data, isPending, isError, errorMessage } = useGetChildQuizAttempt(target?.childId, target?.itemId);

  return (
    <Dialog
      open={Boolean(target)}
      onOpenChange={(open) => {
        if (!open) target?.close();
      }}
    >
      <DialogContent className={'bg-card sm:max-w-lg'}>
        <DialogBody>
          <DialogHeader>
            <DialogTitle>{data?.quiz.name ?? 'Quiz'}</DialogTitle>
          </DialogHeader>
          <DialogDescription className={'sr-only'}>Szczegóły rozwiązanego quizu</DialogDescription>

          {isPending ? (
            <div className={'flex flex-col gap-3 px-4 pb-4'}>
              <Skeleton className={'h-10 w-full'} />
              <Skeleton className={'h-24 w-full'} />
            </div>
          ) : isError || !data ? (
            <div className={'px-4 pb-4'}>
              <Alert variant={'destructive'}>
                <TriangleAlertIcon />
                <AlertDescription>{errorMessage?.detail ?? 'Nie udało się pobrać quizu.'}</AlertDescription>
              </Alert>
            </div>
          ) : (
            <QuizAttemptDetails attempt={data} />
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

interface QuizAttemptDetailsProps {
  attempt: ParentChildQuizAttempt;
}

function QuizAttemptDetails(quizAttemptDetailsProps: QuizAttemptDetailsProps) {
  const { attempt } = quizAttemptDetailsProps;
  const { quiz, score, completedAt, grade } = attempt;

  const completedDate = completedAt ? parseISO(completedAt) : null;
  const deadlineDate = quiz.deadline ? parseISO(quiz.deadline) : null;
  // `maxAttempts` counts attempts only above zero — zero is the quiz builder's
  // way of saying "no limit", which is not a figure worth a slot of its own.
  const hasAttemptLimit = quiz.maxAttempts > 0;

  return (
    <div className={'flex flex-col gap-5 overflow-auto px-4 pb-4'}>
      <div className={'flex flex-wrap items-start gap-x-6 gap-y-3'}>
        {completedDate ? <ParentStat label={'Rozwiązano'}>{format(completedDate, DATE_FORMAT)}</ParentStat> : null}

        <QAttemptResultCard score={score ?? null} maxPoints={quiz.points} className={'w-auto gap-6'} />

        <ParentGradeValue grade={grade} />

        {deadlineDate ? <ParentStat label={'Termin'}>{format(deadlineDate, DATE_FORMAT)}</ParentStat> : null}

        <ParentStat label={'Maksimum punktów'}>{quiz.points}</ParentStat>

        {hasAttemptLimit ? <ParentStat label={'Dozwolone podejścia'}>{quiz.maxAttempts}</ParentStat> : null}
      </div>

      {quiz.desc ? (
        <section className={'flex flex-col gap-1.5'}>
          <h3 className={'text-muted-foreground text-xs font-medium'}>Opis quizu</h3>
          <ParentRichText content={quiz.desc} />
        </section>
      ) : null}
    </div>
  );
}
